import { FormattedMessage } from 'react-intl';
import { useMatch, useParams } from 'react-router-dom-v5-compat';

import config from '@/config';
import { SensitiveField } from '@/modules/api/codegen';
import { CopyButton, PasswordVisualisation } from '@/modules/ui';
import { hasReadPermission } from '@/utilities/permission';
import { isEmptyOrUndefined } from '@/utilities/string';

import type { PasswordTypeSecret, Secret, Share } from '../../../types';
import { SecretFieldSection } from '../../SecretFieldSection';
import { SecretPasskeyRecord } from '../../SecretPasskeyRecord';
import { SecretUrl } from '../../SecretUrl';
import { TOTP } from '../../TOTP';
import { EmptyContentGuard } from '../EmptyContentGuard';

export interface PasswordSectionsProps {
    secret: PasswordTypeSecret;
}

export const PasswordSections = ({ secret }: PasswordSectionsProps) => {
    const { web, username, userPermissions, password, passkey, sensitiveFields } = secret;

    const hasRead = hasReadPermission(userPermissions);
    const isPublicSharingPage = useMatch(config.routes.share);

    // We need to get the secret ID from the URL because this component can be used in both secret detail and shared secret detail pages.
    // Shared secret page uses share ID for TOTP instead of secret ID.
    const { id: secretOrShareId } = useParams<{ id: Secret['id'] | Share['id'] }>();

    return (
        <EmptyContentGuard>
            {!isEmptyOrUndefined(web) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.web' />}>
                    <CopyButton text={web}>
                        <SecretUrl web={web} />
                    </CopyButton>
                </SecretFieldSection>
            )}

            {!isEmptyOrUndefined(username) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.username' />}>
                    <CopyButton text={username} />
                </SecretFieldSection>
            )}

            {sensitiveFields?.includes(SensitiveField.password) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.password' />}>
                    <CopyButton disabled={!hasRead} type='password' text={password!}>
                        <PasswordVisualisation password={password!} />
                    </CopyButton>
                </SecretFieldSection>
            )}

            {sensitiveFields?.includes(SensitiveField.TOTPsecret) && (
                <SecretFieldSection heading={<FormattedMessage id='secret.totp' />}>
                    <TOTP id={secretOrShareId!} isPubliclyShared={Boolean(isPublicSharingPage)} />
                </SecretFieldSection>
            )}

            {passkey?.createdAt && <SecretPasskeyRecord createdAt={passkey.createdAt} />}
        </EmptyContentGuard>
    );
};
